import { Loader } from 'lucide-react';

export const COLORS = {
  ink: '#0d1f15',
  green: '#1f3d2b',
  sage: '#5a7062',
  gold: '#d97736',
  cream: '#faf7f0',
  creamCard: '#f6f4ef',
};

export const StatCard = ({ label, value, icon: Icon, testId, accent = COLORS.green }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-2xl border border-[#e8dfc7] p-5 flex flex-col gap-2 shadow-sm"
  >
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5a7062]">
        {label}
      </span>
      {Icon && (
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${accent}1A` }}
        >
          <Icon size={15} style={{ color: accent }} />
        </div>
      )}
    </div>
    <span
      className="text-3xl tracking-tight"
      style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: COLORS.ink }}
    >
      {value}
    </span>
  </div>
);

export const SectionCard = ({ title, subtitle, children, testId, actions }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-[#e8dfc7] shadow-sm">
    {(title || actions) && (
      <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-[#f0ebe0]">
        <div>
          {title && (
            <h3
              className="text-lg tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: COLORS.ink }}
            >
              {title}
            </h3>
          )}
          {subtitle && <p className="text-sm text-[#5a7062] mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

export const Badge = ({ children, tone = 'green' }) => {
  const tones = {
    green: 'bg-[#1f3d2b]/10 text-[#1f3d2b]',
    gold: 'bg-[#d97736]/15 text-[#a85820]',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

export const EmptyState = ({ label = 'Nothing to show yet.', testId }) => (
  <div data-testid={testId} className="text-center py-10 text-sm text-[#5a7062]">
    {label}
  </div>
);

export const Spinner = ({ label = 'Loading…' }) => (
  <div className="flex items-center gap-2 text-sm text-[#5a7062] py-10 justify-center">
    <Loader className="animate-spin" size={16} /> {label}
  </div>
);

export const PrimaryButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    className="inline-flex items-center gap-2 bg-[#d97736] hover:bg-[#bd6224] text-white font-bold rounded-xl px-4 py-2.5 text-sm transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
    {...rest}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    className="inline-flex items-center gap-2 bg-white border border-[#d8cfb4] hover:border-[#1f3d2b] text-[#1f3d2b] font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    {...rest}
  >
    {children}
  </button>
);

export const DangerButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    className="inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    {...rest}
  >
    {children}
  </button>
);

export const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};
